/**
 * @file Catalogue des 65 armes (D..ZZZ) issu de data/gun_balance_sheet.tsv ; builder + index par id/grade.
 */
import type { GradeId, WeaponBehavior } from '@shared/enums';
import type { GunSilhouette, WeaponDef } from './types';

// [grade, slot, sheetId, behavior, baseDamage, interval, param, silhouette, barrels, size, scope, bipod, drum, nameEn, nameFr]
// Note : la feuille d'origine reutilise les ids 45..49 pour XXX, Z, ZZ et ZZZ (doublon) ; l'id unique est `${grade}-${slot}`.
// param : single = cone de dispersion (deg*2 apres `spreadParamScale`) ; shotgun = plombs supplementaires ;
//         explosive/missile/blastSniper = rayon d'explosion (x `explosionRadiusScale`) ; pierce/laser = 0.
type Row = [
  GradeId, number, number, WeaponBehavior, number, number, number, GunSilhouette,
  number, number, 0 | 1 | 2, 0 | 1, 0 | 1, string, string,
];

const ROWS: Row[] = [
  ['D', 0, 0, 'single', 100, 0.6, 0, 'pistol', 1, 1, 0, 0, 0, "Beginner Pistol", "Pistolet débutant"],
  ['D', 1, 1, 'single', 80, 0.5, 0, 'pistol', 1, 1, 0, 0, 0, "Small Pistol", "Petit pistolet"],
  ['D', 2, 2, 'single', 50, 0.3, 30, 'smg', 1, 1, 0, 0, 0, "Light MG", "Mitrailleuse légère"],
  ['D', 3, 3, 'shotgun', 80, 1, 2, 'shotgun', 1, 1, 0, 0, 0, "Short Shotgun", "Fusil à canon court"],
  ['D', 4, 4, 'shotgun', 90, 1.1, 2, 'doubleShotgun', 2, 1, 0, 0, 0, "Double Shotgun", "Fusil double"],
  ['C', 0, 5, 'single', 200, 0.6, 0, 'revolver', 1, 1.15, 0, 0, 0, "Revolver", "Revolver"],
  ['C', 1, 6, 'single', 100, 0.3, 30, 'smg', 1, 1.15, 0, 0, 0, "Assault SMG", "Mitraillette d'assaut"],
  ['C', 2, 7, 'shotgun', 240, 1.1, 2, 'pumpShotgun', 1, 1.15, 0, 0, 0, "Pump Shotgun", "Fusil à pompe"],
  ['C', 3, 8, 'pierce', 150, 1, 0, 'boltRifle', 1, 1.2, 0, 0, 0, "Hunter's Rifle", "Carabine du chasseur"],
  ['C', 4, 9, 'pierce', 110, 0.7, 0, 'rifle', 1, 1.15, 0, 0, 0, "Scout Rifle", "Fusil d'éclaireur"],
  ['B', 0, 10, 'single', 165, 0.25, 30, 'gatling', 4, 1.5, 0, 0, 0, "Chaingun", "Chaîne-gun"],
  ['B', 1, 11, 'single', 200, 0.3, 30, 'minigun', 3, 1.5, 0, 0, 0, "Minigun", "Minigun"],
  ['B', 2, 12, 'shotgun', 250, 1, 3, 'drumShotgun', 1, 1.5, 0, 0, 1, "Combat Shotgun", "Fusil de combat"],
  ['B', 3, 13, 'pierce', 250, 0.7, 0, 'railgun', 1, 1.5, 0, 0, 0, "Railgun", "Canon à rails"],
  ['B', 4, 14, 'explosive', 120, 0.7, 5, 'grenadeLauncher', 1, 1.5, 0, 0, 0, "Grenade Launcher", "Lance-grenades"],
  ['A', 0, 15, 'single', 300, 0.2, 30, 'gatling', 6, 1.9, 0, 0, 0, "Vulcan", "Vulcain"],
  ['A', 1, 16, 'shotgun', 350, 0.8, 3, 'drumShotgun', 1, 1.9, 0, 0, 1, "Drum Shotgun", "Fusil à tambour"],
  ['A', 2, 17, 'pierce', 500, 0.6, 0, 'rifle', 1, 1.9, 0, 0, 0, "Magnum Rifle", "Fusil Magnum"],
  ['A', 3, 18, 'explosive', 300, 0.7, 5.5, 'cannon', 1, 1.9, 0, 0, 0, "Plasma Cannon", "Canon à plasma"],
  ['A', 4, 19, 'laser', 200, 1, 0, 'sniper', 1, 1.9, 1, 1, 0, "Tactical Sniper", "Sniper tactique"],
  ['S', 0, 20, 'single', 500, 0.05, 30, 'gatling', 8, 2, 0, 0, 0, "Gatling", "Gatling"],
  ['S', 1, 21, 'pierce', 1000, 0.2, 0, 'rifle', 1, 2, 0, 0, 0, "Force Rifle", "Fusil de force"],
  ['S', 2, 22, 'explosive', 800, 0.3, 6, 'cannon', 1, 2, 0, 0, 0, "Flame Cannon", "Canon à flammes"],
  ['S', 3, 23, 'laser', 400, 0.4, 0, 'sniper', 1, 2, 1, 0, 0, "Ghost Sniper", "Sniper fantôme"],
  ['S', 4, 24, 'missile', 600, 0.4, 9, 'launcher', 1, 2, 0, 0, 0, "Scorpion Missile", "Missile Scorpion"],
  ['SS', 0, 25, 'pierce', 6000, 0.2, 0, 'rifle', 1, 2.3, 0, 0, 0, "Piercer SS", "Perforant SS"],
  ['SS', 1, 26, 'explosive', 2800, 0.3, 6.5, 'cannon', 1, 2.3, 0, 0, 0, "Blaster SS", "Explosif SS"],
  ['SS', 2, 27, 'laser', 1400, 0.3, 0, 'sniper', 1, 2.3, 1, 0, 0, "Laser SS", "Laser SS"],
  ['SS', 3, 28, 'missile', 1600, 0.35, 10, 'launcher', 2, 2.3, 0, 0, 0, "Missile SS", "Missile SS"],
  ['SS', 4, 29, 'blastSniper', 1600, 0.35, 5.5, 'energyCannon', 1, 2.3, 1, 0, 0, "Blast Laser SS", "Laser explosif SS"],
  ['SSS', 0, 30, 'pierce', 24000, 0.1, 0, 'rifle', 1, 2.5, 0, 0, 0, "Piercer SSS", "Perforant SSS"],
  ['SSS', 1, 31, 'explosive', 11200, 0.15, 7, 'cannon', 1, 2.5, 0, 0, 0, "Blaster SSS", "Explosif SSS"],
  ['SSS', 2, 32, 'laser', 5600, 0.15, 0, 'sniper', 1, 2.5, 1, 0, 0, "Laser SSS", "Laser SSS"],
  ['SSS', 3, 33, 'missile', 6400, 0.2, 11, 'launcher', 4, 2.5, 0, 0, 0, "Missile SSS", "Missile SSS"],
  ['SSS', 4, 34, 'blastSniper', 6400, 0.2, 6, 'energyCannon', 1, 2.5, 1, 0, 0, "Blast Laser SSS", "Laser explosif SSS"],
  ['X', 0, 35, 'pierce', 90000, 0.05, 0, 'rifle', 3, 2.7, 0, 0, 0, "Piercer X", "Perforant X"],
  ['X', 1, 36, 'explosive', 42000, 0.07, 9, 'cannon', 1, 2.7, 0, 0, 0, "Blaster X", "Explosif X"],
  ['X', 2, 37, 'laser', 21000, 0.07, 0, 'sniper', 1, 2.7, 2, 0, 0, "Laser X", "Laser X"],
  ['X', 3, 38, 'missile', 24000, 0.1, 13, 'launcher', 6, 2.7, 0, 0, 0, "Missile X", "Missile X"],
  ['X', 4, 39, 'blastSniper', 24000, 0.1, 8, 'energyCannon', 1, 2.7, 2, 0, 0, "Blast Laser X", "Laser explosif X"],
  ['XX', 0, 40, 'pierce', 360000, 0.05, 0, 'rifle', 1, 2.8, 0, 0, 0, "Piercer XX", "Perforant XX"],
  ['XX', 1, 41, 'explosive', 168000, 0.07, 9, 'cannon', 1, 2.8, 0, 0, 0, "Blaster XX", "Explosif XX"],
  ['XX', 2, 42, 'laser', 84000, 0.07, 0, 'sniper', 1, 2.8, 2, 0, 0, "Laser XX", "Laser XX"],
  ['XX', 3, 43, 'missile', 96000, 0.1, 13, 'launcher', 8, 2.8, 0, 0, 0, "Missile XX", "Missile XX"],
  ['XX', 4, 44, 'blastSniper', 96000, 0.1, 8, 'energyCannon', 1, 2.8, 2, 0, 0, "Blast Laser XX", "Laser explosif XX"],
  ['XXX', 0, 45, 'pierce', 1350000, 0.05, 0, 'rifle', 4, 2.9, 0, 0, 0, "Piercer XXX", "Perforant XXX"],
  ['XXX', 1, 46, 'explosive', 630000, 0.07, 9, 'cannon', 1, 2.9, 0, 0, 0, "Blaster XXX", "Explosif XXX"],
  ['XXX', 2, 47, 'laser', 315000, 0.07, 0, 'sniper', 1, 2.9, 2, 0, 0, "Laser XXX", "Laser XXX"],
  ['XXX', 3, 48, 'missile', 360000, 0.1, 13, 'launcher', 10, 2.9, 0, 0, 0, "Missile XXX", "Missile XXX"],
  ['XXX', 4, 49, 'blastSniper', 360000, 0.1, 8, 'energyCannon', 1, 2.9, 2, 0, 0, "Blast Laser XXX", "Laser explosif XXX"],
  ['Z', 0, 45, 'pierce', 15000000, 0.05, 0, 'rifle', 4, 3, 0, 0, 0, "Piercer Z", "Perforant Z"],
  ['Z', 1, 46, 'explosive', 7000000, 0.07, 9, 'cannon', 1, 3, 0, 0, 0, "Blaster Z", "Explosif Z"],
  ['Z', 2, 47, 'laser', 3500000, 0.07, 0, 'sniper', 1, 3, 2, 0, 0, "Laser Z", "Laser Z"],
  ['Z', 3, 48, 'missile', 4000000, 0.1, 13, 'launcher', 10, 3, 0, 0, 0, "Missile Z", "Missile Z"],
  ['Z', 4, 49, 'blastSniper', 4000000, 0.1, 8, 'energyCannon', 1, 3, 2, 0, 0, "Blast Laser Z", "Laser explosif Z"],
  ['ZZ', 0, 45, 'pierce', 300000000, 0.05, 0, 'rifle', 4, 3.1, 0, 0, 0, "Piercer ZZ", "Perforant ZZ"],
  ['ZZ', 1, 46, 'explosive', 140000000, 0.07, 9, 'cannon', 1, 3.1, 0, 0, 0, "Blaster ZZ", "Explosif ZZ"],
  ['ZZ', 2, 47, 'laser', 70000000, 0.07, 0, 'sniper', 1, 3.1, 2, 0, 0, "Laser ZZ", "Laser ZZ"],
  ['ZZ', 3, 48, 'missile', 80000000, 0.1, 13, 'launcher', 10, 3.1, 0, 0, 0, "Missile ZZ", "Missile ZZ"],
  ['ZZ', 4, 49, 'blastSniper', 80000000, 0.1, 8, 'energyCannon', 1, 3.1, 2, 0, 0, "Blast Laser ZZ", "Laser explosif ZZ"],
  ['ZZZ', 0, 45, 'pierce', 7500000000, 0.05, 0, 'rifle', 4, 3.2, 0, 0, 0, "Piercer ZZZ", "Perforant ZZZ"],
  ['ZZZ', 1, 46, 'explosive', 3500000000, 0.07, 9, 'cannon', 1, 3.2, 0, 0, 0, "Blaster ZZZ", "Explosif ZZZ"],
  ['ZZZ', 2, 47, 'laser', 1750000000, 0.07, 0, 'sniper', 1, 3.2, 2, 0, 0, "Laser ZZZ", "Laser ZZZ"],
  ['ZZZ', 3, 48, 'missile', 2000000000, 0.1, 13, 'launcher', 10, 3.2, 0, 0, 0, "Missile ZZZ", "Missile ZZZ"],
  ['ZZZ', 4, 49, 'blastSniper', 2000000000, 0.1, 8, 'energyCannon', 1, 3.2, 2, 0, 0, "Blast Laser ZZZ", "Laser explosif ZZZ"],
];

const toDef = (r: Row): WeaponDef => ({
  id: `${r[0]}-${r[1]}`,
  sheetId: r[2],
  grade: r[0],
  slot: r[1],
  behavior: r[3],
  baseDamage: r[4],
  interval: r[5],
  param: r[6],
  name: { en: r[13], fr: r[14] },
  visual: { silhouette: r[7], barrels: r[8], size: r[9], scope: r[10], bipod: r[11] === 1, drumMag: r[12] === 1 },
});

/** Catalogue ordonne (grade croissant puis slot). */
export const WEAPONS: readonly WeaponDef[] = ROWS.map(toDef);

export const WEAPON_BY_ID: ReadonlyMap<string, WeaponDef> = new Map(WEAPONS.map((w) => [w.id, w]));

export const WEAPONS_BY_GRADE: ReadonlyMap<GradeId, readonly WeaponDef[]> = (() => {
  const m = new Map<GradeId, WeaponDef[]>();
  for (const w of WEAPONS) {
    const list = m.get(w.grade) ?? [];
    list.push(w);
    m.set(w.grade, list);
  }
  return m;
})();

export const getWeaponDef = (id: string): WeaponDef => {
  const def = WEAPON_BY_ID.get(id);
  if (!def) throw new Error(`Unknown weapon def: ${id}`);
  return def;
};

/** Arme equipee au demarrage d'une nouvelle partie (pistolet debutant) et arme de coffre offerte. */
export const STARTER_WEAPON_ID = 'D-0';
export const STARTER_CHEST_WEAPON_IDS: readonly string[] = ['D-3'];
